import { DynamicDataList, ImgElement } from '@axinom/mosaic-ui';
import { IdentityProvider } from 'generated/id-service-env-administration';
import React from 'react';
import disabled from '../../../../../../images/status/disabled.svg';
import enabled from '../../../../../../images/status/enabled.svg';
import type { FormDataIdProvider } from '../../IdentityProviders.types';
import axinomAuthLogo from '../defaultImages/axinom_auth_logo.svg';
import googleLogo from '../defaultImages/google_logo.svg';
import imageNotFound from '../defaultImages/image_not_found.svg';
import azureLogo from '../defaultImages/microsoft_logo.svg';
import mosaicLogo from '../defaultImages/mosaic_logo.svg';
import { useGenerateInlineMenuActions } from '../helpers/useGenerateInlineMenuActions';
import classes from './Providers.module.scss';

interface ExternalProvidersProps {
  value: FormDataIdProvider[];
  onChange: (value: FormDataIdProvider[]) => void;
}

export const ExternalProviders: React.FC<ExternalProvidersProps> = ({
  value,
  onChange,
}) => {
  const idpLabelMap: Record<string, string> = Object.entries(
    IdentityProvider,
  ).reduce((idpLabelMap, [key, value]) => {
    idpLabelMap[value] =
      value === IdentityProvider.AzureAd
        ? 'Microsoft Entra ID (Azure AD)'
        : key;
    return idpLabelMap;
  }, {} as Record<string, string>);

  return (
    <DynamicDataList<FormDataIdProvider>
      columns={[
        {
          propertyName: 'idpImageUrl',
          label: 'Logo',
          size: '70px',
          render: (val, data) => idpConfigImageRenderer(val as string, data),
        },
        {
          propertyName: 'idpId',
          label: 'Provider',
          render: (val) => <div>{idpLabelMap[val as string]}</div>,
        },
        {
          propertyName: 'displayName',
          label: 'Display Name',
          render: (val, data) =>
            idpConfigDisplayNameRenderer(val as string, data),
        },
        {
          propertyName: 'status',
          label: 'Status',
          render: (val) => idpConfigStatusRenderer(val as string),
        },
        {
          key: 'spacer',
          label: '',
          render: () => null,
          size: 'calc(50px - var(--dynamic-list-column-gap, 8px))',
        },
      ]}
      allowNewData={false}
      showHeader={false}
      positionPropertyName="sortOrder"
      value={value}
      onChange={onChange}
      stickyHeader={false}
      inlineMenuActions={useGenerateInlineMenuActions}
    />
  );
};

const idpConfigDisplayNameRenderer = (
  val: string,
  data: FormDataIdProvider,
): JSX.Element => {
  let displayName: string;
  if (data.useCustomBranding) {
    displayName = val;
  } else {
    switch (data.idpId) {
      case IdentityProvider.AzureAd:
        displayName = 'Sign in with Microsoft';
        break;
      case IdentityProvider.Google:
        displayName = 'Sign in with Google';
        break;
      case IdentityProvider.Axinom:
        displayName = 'Sign in with Axinom';
        break;
      default:
        displayName = '';
        break;
    }
  }

  return <div>{displayName}</div>;
};

const idpConfigStatusRenderer = (val: string): JSX.Element => {
  let status;

  switch (val) {
    case 'ENABLED':
      status = 'Enabled';
      break;
    case 'DISABLED':
      status = 'Disabled';
      break;
    case 'NOT_CONFIGURED':
    default:
      status = 'Not Configured';
      break;
  }
  return (
    <div className={classes.status_container}>
      <ImgElement
        src={val === 'ENABLED' ? enabled : disabled}
        style={{ width: '20px', height: '20px' }}
        decorative={true}
      />
      <div className={classes.status}>{status}</div>
    </div>
  );
};

const idpConfigImageRenderer = (
  val: string,
  data: FormDataIdProvider,
): JSX.Element => {
  let imageUrl;
  if (data.useCustomBranding) {
    switch (val) {
      case 'DEFAULT_GOOGLE':
        imageUrl = googleLogo;
        break;
      case 'DEFAULT_AXINOM':
        imageUrl = axinomAuthLogo;
        break;
      case 'DEFAULT_AZURE':
        imageUrl = azureLogo;
        break;
      default:
        imageUrl = val;
        break;
    }
  } else {
    switch (data.idpId) {
      case IdentityProvider.Google:
        imageUrl = googleLogo;
        break;
      case IdentityProvider.Axinom:
        imageUrl = axinomAuthLogo;
        break;
      case IdentityProvider.AzureAd:
        imageUrl = azureLogo;
        break;
      case IdentityProvider.Custom:
        imageUrl = '';
        break;
      default:
        imageUrl = mosaicLogo;
        break;
    }
  }

  return (
    <div className={classes.status_container}>
      <ImgElement
        src={imageUrl}
        style={{ width: '30px', height: '30px' }}
        onError={({ currentTarget }) => {
          currentTarget.onerror = null;
          currentTarget.src = imageNotFound;
        }}
        decorative={true}
      />
    </div>
  );
};
